import { useState, useEffect } from "react";
import { Autocomplete, TextField, CircularProgress } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { uomsApi, type UomDto } from "@/api/endpoints";

interface UomPickerProps {
  value: UomDto | null;
  onChange: (v: UomDto | null) => void;
  /** When parent has only an ID (e.g. editing existing item), the picker resolves it to a UomDto. */
  initialId?: string | null;
  label?: string;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
}

function useDebouncedValue(value: string, delay = 200) {
  const [d, setD] = useState(value);
  useEffect(() => {
    const h = setTimeout(() => setD(value), delay);
    return () => clearTimeout(h);
  }, [value, delay]);
  return d;
}

export function UomPicker({
  value, onChange, initialId, label = "Base UoM",
  required, error, helperText, disabled,
}: UomPickerProps) {
  // Two separate strings on purpose. `input` is whatever the textbox shows — which MUI also
  // rewrites to the selected option's label whenever the value changes (reason "reset").
  // `search` is only what the user actually typed. Feeding the reset label back into the query
  // made the picker oscillate on edit: the label "EA — Each" matched no row, the option list
  // emptied, the resolved value fell out of it and cleared the field, which reset the input to
  // "" and reloaded the list — the "jumping" base UoM.
  const [input, setInput] = useState("");
  const [search, setSearch] = useState("");
  const debounced = useDebouncedValue(search);

  // Load all UoMs (small set, pageSize=100 is fine for the master)
  const q = useQuery({
    queryKey: ["uoms-picker", debounced],
    queryFn: () => uomsApi.list({ search: debounced || undefined, page: 1, pageSize: 100 }),
    staleTime: 5 * 60_000,
  });

  // Resolve initialId against the unfiltered master rather than the search results, so the
  // current selection survives whatever is typed into the box.
  const resolveQuery = useQuery({
    queryKey: ["uoms-picker", "resolve", initialId],
    queryFn: () => uomsApi.list({ page: 1, pageSize: 500 }),
    enabled: !value && !!initialId,
    staleTime: 5 * 60_000,
  });
  const resolved = value
    ?? (initialId ? resolveQuery.data?.items.find(u => u.id === initialId) ?? null : null);

  // The resolved row need not be in the current (searched) page; keep it in options so MUI
  // doesn't discard the value as "not one of the options".
  const options = q.data?.items ?? [];
  const withValue = resolved && !options.some(o => o.id === resolved.id)
    ? [resolved, ...options]
    : options;

  return (
    <Autocomplete<UomDto>
      value={resolved}
      options={withValue}
      inputValue={input}
      onInputChange={(_, v, reason) => {
        setInput(v);
        // Only typing drives the query. "reset" carries the selected option's label and
        // "clear" an empty string; neither is a search the user asked for.
        setSearch(reason === "input" ? v : "");
      }}
      onChange={(_, v) => onChange(v)}
      disabled={disabled}
      loading={q.isLoading}
      getOptionLabel={(o) => `${o.code} — ${o.name}`}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      filterOptions={(x) => x}
      size="small" fullWidth
      renderInput={(params) => (
        <TextField {...params} label={`${label}${required ? " *" : ""}`}
          error={error} helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: <>
              {q.isLoading ? <CircularProgress size={16} /> : null}
              {params.InputProps.endAdornment}
            </>,
          }} />
      )}
    />
  );
}
